// AYA Solidarité — reproduction stricte de la maquette de référence (dense, mobile-first).
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Heart, Radio, Users, ShieldCheck, CalendarDays, Clock, MapPin, Bell,
  ArrowRight, ChevronRight, Home, GraduationCap, Plus, BookOpen, BarChart3, HandHeart,
} from "lucide-react";
import FlagCi from "@/components/FlagCi";
import ExpHeader from "./ExpHeader";
import AyaMenu from "@/components/AyaMenu";

const IMG = "/aya/solidarite";
const ctx = (m) => `/aya-prive?ctx=${encodeURIComponent(m)}`;

const PILLARS = [
  { id: "aide", Icon: Heart, color: "#F87171", fill: "#F87171", title: "Aide directe", sub: "Personnes dans le besoin" },
  { id: "lives", Icon: Radio, color: "#A78BFA", title: "Lives Solidarité", sub: "En direct sur AYA" },
  { id: "communaute", Icon: Users, color: "#22D3EE", title: "Communauté", sub: "Bénévoles et partenaires" },
  { id: "transparence", Icon: ShieldCheck, color: "#34D399", title: "Transparence", sub: "Suivi et impact réel" },
];

const TABS = [
  { id: "une", label: "À la une", target: "sol-next-live" },
  { id: "lives", label: "Prochains lives", target: "sol-autres-lives" },
  { id: "aidees", label: "Personnes aidées", target: "sol-aides" },
  { id: "aider", label: "Comment aider", target: "sol-cta" },
  { id: "faq", label: "FAQ", target: "sol-cta" },
];

const NEXT_LIVES = [
  { id: "l1", day: "SAM", num: "05", month: "OCT.", time: "18h00", place: "Yopougon", theme: "Soutien scolaire et fournitures" },
  { id: "l2", day: "SAM", num: "12", month: "OCT.", time: "18h00", place: "Abobo", theme: "Aide médicale et équipements" },
  { id: "l3", day: "SAM", num: "26", month: "OCT.", time: "18h00", place: "Adjamé", theme: "Aide alimentaire et kits familiaux" },
];

const HELPED = [
  { id: "kouassi", img: `${IMG}/sol_ben_famille.jpg`, Icon: Home, color: "#34D399", name: "Famille Kouassi", cat: "Logement" },
  { id: "etudiant", img: `${IMG}/sol_ben_etudiant.jpg`, Icon: GraduationCap, color: "#22D3EE", name: "Jeune étudiant", cat: "Scolarité" },
  { id: "awa", img: `${IMG}/sol_ben_awa.jpg`, Icon: Plus, color: "#F87171", name: "Awa Koné", cat: "Soins médicaux" },
  { id: "mariama", img: `${IMG}/sol_ben_mariama.jpg`, Icon: BookOpen, color: "#A78BFA", name: "Mariama", cat: "Formation" },
  { id: "diaby", img: `${IMG}/sol_ben_diaby.jpg`, Icon: BarChart3, color: "#34D399", name: "M. Diaby", cat: "Activité génératrice de revenus" },
];

export default function SolidaritePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("une");
  const [menuOpen, setMenuOpen] = useState(false);

  const pickTab = (t) => {
    setTab(t.id);
    document.getElementById(t.target)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={S.page} data-testid="solidarite-page">
      <AyaMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
      <ExpHeader title="AYA Solidarité" testid="solidarite-header" onBell={() => setMenuOpen(true)} onMore={() => setMenuOpen(true)} />

      <div style={S.container}>
        {/* ── HERO ── */}
        <div style={S.hero} data-testid="solidarite-hero">
          <img src={`${IMG}/sol_hero.jpg`} alt="AYA Solidarité — avec Apoutchou Nationale" style={S.heroImg} />
          <div style={S.heroOverlay} />
          <div style={S.heroContent}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
              <div>
                <div style={S.heroAya}>AYA</div>
                <div style={S.heroSol}>SOLIDARITÉ</div>
              </div>
              <FlagCi size={22} style={{ marginTop: 8 }} />
            </div>
            <p style={S.heroSlogan}>Ensemble<br />pour une Côte d'Ivoire<br />plus solidaire</p>
          </div>
          <div style={S.heroSign} aria-hidden="true">
            <div style={S.heroSignTxt}>Avec<br />Apoutchou<br />Nationale</div>
            <svg width="86" height="10" viewBox="0 0 92 12" style={{ display: "block", margin: "1px auto 0" }}>
              <path d="M2 8 C 22 3, 54 1.5, 90 5 C 58 6.5, 24 9, 3 10.5 Z" fill="#2FCF62" />
            </svg>
          </div>
        </div>

        {/* ── 4 PILIERS ── */}
        <div style={S.pillarGrid} data-testid="solidarite-pillars">
          {PILLARS.map((p) => (
            <button key={p.id} style={S.pillar} data-testid={`sol-pillar-${p.id}`} onClick={() => pickTab(TABS[p.id === "lives" ? 1 : p.id === "communaute" ? 3 : p.id === "transparence" ? 2 : 0])}>
              <p.Icon size={22} color={p.color} fill={p.fill || "none"} strokeWidth={2.2} />
              <span style={S.pillarTitle}>{p.title}</span>
              <span style={S.pillarSub}>{p.sub}</span>
            </button>
          ))}
        </div>

        {/* ── TABS ── */}
        <div style={S.tabsWrap} data-testid="solidarite-tabs">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`sol-tab-${t.id}`} onClick={() => pickTab(t)}
              style={{ ...S.tab, ...(tab === t.id ? S.tabOn : {}) }}>
              {t.label}
              {tab === t.id && <span style={S.tabBar} />}
            </button>
          ))}
        </div>

        {/* ── PROCHAIN LIVE ── */}
        <div id="sol-next-live" style={S.secHead}>
          <h2 style={S.secTitle}>Prochain live Solidarité</h2>
          <button style={S.secMore} data-testid="sol-lives-see-all" onClick={() => pickTab(TABS[1])}>Voir tous <ArrowRight size={13} /></button>
        </div>
        <div style={S.liveCard} data-testid="sol-next-live-card">
          <div style={S.liveImgBox}>
            <img src={`${IMG}/sol_live.jpg`} alt="Live AYA Solidarité" style={S.liveImg} loading="lazy" />
            <span style={S.liveBadge}>• LIVE</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={S.liveTitle}>AYA Solidarité<br />avec Apoutchou Nationale</h3>
            <div style={S.liveMetaRow}>
              <span style={S.liveMeta}><CalendarDays size={12} color="#34D399" /> Samedi 28 septembre 2026</span>
              <span style={S.liveMeta}><Clock size={12} color="#34D399" /> 18h00</span>
              <span style={S.liveMeta}><MapPin size={12} color="#34D399" /> Abidjan</span>
            </div>
            <p style={S.liveDesc}>Soutien aux familles en difficulté : distribution d'aides, témoignages et surprises.</p>
            <div style={S.liveFoot}>
              <span style={S.avatarStack}>
                {[HELPED[1], HELPED[2], HELPED[3], HELPED[4]].map((h, i) => (
                  <img key={h.id} src={h.img} alt="" style={{ ...S.stackImg, marginLeft: i === 0 ? 0 : -6 }} />
                ))}
              </span>
              <span style={S.interested}>12,4K personnes intéressées</span>
              <button style={S.remindBtn} data-testid="sol-remind-btn" onClick={() => toast.success("Rappel programmé pour le live ✓")}>
                <Bell size={12} /> Me rappeler
              </button>
            </div>
          </div>
        </div>

        {/* ── AUTRES LIVES ── */}
        <div id="sol-autres-lives" style={S.secHead}>
          <h2 style={S.secTitle}>Autres lives à venir</h2>
          <button style={S.secMore} data-testid="sol-autres-see-all" onClick={() => navigate(ctx("Je veux voir tous les prochains lives AYA Solidarité."))}>Voir tous <ArrowRight size={13} /></button>
        </div>
        <div style={S.hScroll} data-testid="sol-autres-lives-row">
          {NEXT_LIVES.map((l) => (
            <button key={l.id} style={S.upCard} data-testid={`sol-upcoming-${l.id}`} onClick={() => toast.success(`Rappel programmé — ${l.theme} ✓`)}>
              <span style={S.upImgBox}>
                <img src={`${IMG}/sol_live.jpg`} alt="" style={S.upImg} loading="lazy" />
                <span style={S.upDate}>
                  <span style={{ color: "#DC2626", fontSize: 8, fontWeight: 800 }}>{l.day}</span>
                  <span style={{ fontSize: 15, fontWeight: 900, lineHeight: 1 }}>{l.num}</span>
                  <span style={{ fontSize: 8, fontWeight: 800 }}>{l.month}</span>
                </span>
                <span style={{ ...S.liveBadge, top: 6, right: 6, left: "auto", fontSize: 8 }}>• LIVE</span>
              </span>
              <span style={S.upMeta}><Clock size={11} color="#34D399" /> {l.time}</span>
              <span style={S.upMeta}><MapPin size={11} color="#34D399" /> {l.place}</span>
              <span style={S.upTheme}>
                <HandHeart size={12} color="#9DB4A6" style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{l.theme}</span>
                <ChevronRight size={13} color="#9DB4A6" />
              </span>
            </button>
          ))}
        </div>

        {/* ── ILS ONT DÉJÀ ÉTÉ AIDÉS ── */}
        <div id="sol-aides" style={S.secHead}>
          <h2 style={S.secTitle}>Ils ont déjà été aidés</h2>
          <button style={S.secMore} data-testid="sol-helped-see-more" onClick={() => navigate(ctx("Je veux découvrir les personnes déjà aidées par AYA Solidarité et l'impact réel."))}>Voir plus <ArrowRight size={13} /></button>
        </div>
        <div style={S.hScroll} data-testid="sol-helped-row">
          {HELPED.map((h) => (
            <button key={h.id} style={S.helpCard} data-testid={`sol-helped-${h.id}`} onClick={() => navigate(ctx(`Je veux en savoir plus sur l'aide apportée à ${h.name} (${h.cat}) via AYA Solidarité.`))}>
              <img src={h.img} alt={h.name} style={S.helpImg} loading="lazy" />
              <span style={S.helpName}><h.Icon size={12} color={h.color} strokeWidth={2.4} /> {h.name}</span>
              <span style={S.helpCat}>{h.cat}</span>
            </button>
          ))}
        </div>

        {/* ── CTA FINAUX ── */}
        <div id="sol-cta" style={S.ctaGrid} data-testid="sol-cta-section">
          <div style={S.ctaOrange}>
            <HandHeart size={24} color="#fff" />
            <h3 style={S.ctaTitle}>Demander de l'aide</h3>
            <p style={S.ctaDesc}>Vous êtes dans une situation difficile ? Faites une demande d'accompagnement sur AYA.</p>
            <button style={S.ctaBtnWhite} data-testid="sol-cta-help-btn" onClick={() => navigate(ctx("Je suis dans une situation difficile et je souhaite faire une demande d'aide via AYA Solidarité."))}>
              Demander de l'aide <ArrowRight size={14} />
            </button>
          </div>
          <div style={S.ctaGreen}>
            <Users size={24} color="#34D399" />
            <h3 style={S.ctaTitle}>Rejoindre<br />AYA Solidarité</h3>
            <p style={S.ctaDesc}>Devenez bénévole, partenaire ou soutenez nos actions.</p>
            <button style={S.ctaBtnOutline} data-testid="sol-cta-join-btn" onClick={() => navigate(ctx("Je veux rejoindre AYA Solidarité comme bénévole ou partenaire."))}>
              S'inscrire <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "radial-gradient(ellipse at top, #072115 0%, #04120B 60%, #030B07 100%)", color: "#ECF5EE", paddingBottom: 26, overflowX: "hidden" },
  container: { maxWidth: 880, margin: "0 auto", padding: "0 12px", minWidth: 0 },
  hero: { position: "relative", borderRadius: 18, overflow: "hidden", height: 218, marginTop: 2, border: "1px solid rgba(16,185,129,0.25)" },
  heroImg: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "right center" },
  heroOverlay: { position: "absolute", inset: 0, background: "linear-gradient(95deg, rgba(3,20,12,0.92) 24%, rgba(3,20,12,0.42) 58%, rgba(3,20,12,0.12))" },
  heroContent: { position: "relative", padding: "16px 14px" },
  heroAya: { fontSize: 34, fontWeight: 900, color: "#FFFFFF", lineHeight: 0.95, letterSpacing: 1 },
  heroSol: { fontSize: 24, fontWeight: 900, color: "#2FCF62", lineHeight: 1.05, letterSpacing: 0.5 },
  heroSlogan: { margin: "12px 0 0", fontSize: 13.5, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.3 },
  heroSign: { position: "absolute", right: 12, top: 14, transform: "rotate(-8deg)", textAlign: "center", pointerEvents: "none" },
  heroSignTxt: { fontFamily: "'Caveat', cursive", fontWeight: 700, fontSize: 17, lineHeight: 0.95, color: "#FFFFFF", textShadow: "0 1px 8px rgba(0,0,0,0.6)" },
  pillarGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 7, marginTop: 10 },
  pillar: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "12px 5px 10px", borderRadius: 14, background: "rgba(8,38,24,0.65)", border: "1px solid rgba(16,185,129,0.3)", cursor: "pointer", color: "#ECF5EE" },
  pillarTitle: { fontSize: 11, fontWeight: 800, lineHeight: 1.15, textAlign: "center", marginTop: 2 },
  pillarSub: { fontSize: 8.8, color: "#9DB4A6", lineHeight: 1.2, textAlign: "center" },
  tabsWrap: { display: "flex", gap: 2, marginTop: 10, padding: "3px 4px", borderRadius: 13, background: "rgba(8,38,24,0.6)", border: "1px solid rgba(16,185,129,0.22)", overflowX: "auto", scrollbarWidth: "none" },
  tab: { position: "relative", whiteSpace: "nowrap", padding: "9px 11px", background: "none", border: "none", color: "#9DB4A6", fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  tabOn: { color: "#FFFFFF" },
  tabBar: { position: "absolute", left: 10, right: 10, bottom: 3, height: 2.5, borderRadius: 2, background: "#10B981" },
  secHead: { display: "flex", justifyContent: "space-between", alignItems: "center", margin: "14px 0 8px", scrollMarginTop: 56 },
  secTitle: { fontSize: 15.5, fontWeight: 800, margin: 0, color: "#FFFFFF" },
  secMore: { display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", color: "#34D399", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  liveCard: { display: "flex", gap: 10, padding: 10, borderRadius: 16, background: "rgba(8,38,24,0.7)", border: "1px solid rgba(16,185,129,0.35)" },
  liveImgBox: { position: "relative", flex: "0 0 40%", maxWidth: 190, borderRadius: 12, overflow: "hidden", alignSelf: "stretch", minHeight: 128 },
  liveImg: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" },
  liveBadge: { position: "absolute", top: 7, left: 7, padding: "3px 8px", borderRadius: 8, background: "#DC2626", color: "#fff", fontSize: 9, fontWeight: 800, letterSpacing: 0.4 },
  liveTitle: { margin: 0, fontSize: 13.5, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.25 },
  liveMetaRow: { display: "flex", flexWrap: "wrap", gap: "4px 10px", marginTop: 7 },
  liveMeta: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: "#CFE5D6", fontWeight: 600 },
  liveDesc: { margin: "7px 0 0", fontSize: 10, color: "#9DB4A6", lineHeight: 1.4 },
  liveFoot: { display: "flex", alignItems: "center", gap: 6, marginTop: 9, flexWrap: "wrap" },
  avatarStack: { display: "inline-flex", flexShrink: 0 },
  stackImg: { width: 18, height: 18, borderRadius: "50%", objectFit: "cover", border: "1.5px solid #06281A" },
  interested: { fontSize: 9, color: "#CFE5D6", fontWeight: 600, flex: 1, minWidth: 0 },
  remindBtn: { display: "inline-flex", alignItems: "center", gap: 5, height: 30, padding: "0 12px", borderRadius: 999, border: "none", background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", fontSize: 10.5, fontWeight: 800, cursor: "pointer", flexShrink: 0 },
  hScroll: { display: "flex", gap: 9, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 3 },
  upCard: { flex: "0 0 60%", maxWidth: 246, display: "flex", flexDirection: "column", gap: 4, padding: 8, borderRadius: 14, background: "rgba(8,38,24,0.7)", border: "1px solid rgba(16,185,129,0.28)", cursor: "pointer", color: "#ECF5EE", textAlign: "left" },
  upImgBox: { position: "relative", height: 82, borderRadius: 10, overflow: "hidden", display: "block" },
  upImg: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" },
  upDate: { position: "absolute", top: 6, left: 6, display: "flex", flexDirection: "column", alignItems: "center", padding: "3px 7px", borderRadius: 8, background: "#FFFFFF", color: "#0B3B27" },
  upMeta: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, color: "#CFE5D6", fontWeight: 700 },
  upTheme: { display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: "#9DB4A6", fontWeight: 600, lineHeight: 1.25, borderTop: "1px solid rgba(16,185,129,0.18)", paddingTop: 5, marginTop: 1 },
  helpCard: { flex: "0 0 31%", minWidth: 118, maxWidth: 152, display: "flex", flexDirection: "column", gap: 3, padding: 0, background: "none", border: "none", cursor: "pointer", color: "#ECF5EE", textAlign: "left" },
  helpImg: { width: "100%", height: 96, objectFit: "cover", borderRadius: 12, border: "1px solid rgba(16,185,129,0.25)" },
  helpName: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 800, marginTop: 3 },
  helpCat: { fontSize: 9, color: "#9DB4A6", lineHeight: 1.25 },
  ctaGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 9, marginTop: 14, scrollMarginTop: 56 },
  ctaOrange: { display: "flex", flexDirection: "column", gap: 6, padding: "14px 12px 12px", borderRadius: 18, background: "linear-gradient(150deg,#F97316,#EA580C)" },
  ctaGreen: { display: "flex", flexDirection: "column", gap: 6, padding: "14px 12px 12px", borderRadius: 18, background: "rgba(8,38,24,0.8)", border: "1px solid rgba(16,185,129,0.4)" },
  ctaTitle: { margin: 0, fontSize: 14.5, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.2 },
  ctaDesc: { margin: 0, fontSize: 10, color: "rgba(255,255,255,0.86)", lineHeight: 1.4, flex: 1 },
  ctaBtnWhite: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, height: 36, borderRadius: 999, border: "none", background: "#FFFFFF", color: "#C2410C", fontSize: 11.5, fontWeight: 800, cursor: "pointer", marginTop: 4 },
  ctaBtnOutline: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, height: 36, borderRadius: 999, border: "1.5px solid rgba(16,185,129,0.6)", background: "rgba(6,40,26,0.6)", color: "#E8F6EC", fontSize: 11.5, fontWeight: 800, cursor: "pointer", marginTop: 4 },
};
